import React from 'react';
import {
    Badge,
    Button,
    Progress,
} from 'reactstrap';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTasks, faComments, faCircleNotch, faPrint, faCheck, faClose, faPaperPlane, faHistory,
    faFileExcel,
    faEye,
    faFilePdf,
    faEnvelope,
    faDownload,
    faIdBadge,
} from '@fortawesome/free-solid-svg-icons';
import { fileNameFromContentDisposition } from '@s20/common/util/http';
import { httpClient } from '@s20/services/httpClient';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import SlideForm from '../common/forms/SlideForm';
import InvoiceIssuesWidget from '../widgets/InvoiceIssuesWidget';
import CommonContext, { ApiRoutes, ApplicationPermissions, BillingCycles } from '../Common';
import {
    createDataSource,
    createGridOptions,
    DataGrid,
    indexCellRenderer,
    LinkCellRenderer,
    IconCellRenderer,
    VariableLinkCellRenderer,
    TextFilterDefaults,
    DateFilterDefaults,
    getServerSideRowRequest,
} from '../common/dataGrid/DataGrid';
import DataGridSelectFilter from '../common/dataGrid/DataGridSelectFilter';
import DataGridSelectFloatingFilter from '../common/dataGrid/DataGridSelectFloatingFilter';
import DataGridToolbar from '../common/dataGrid/DataGridToolbar';
import {
    FlexRow,
    PageHeading,
    PageWrap,
    toasty,
    onFieldChange,
    FlexCenterRow,
    IconButton,
    SmallButton,
    FormLabel,
    Input,
} from '../common/forms/FormElements';
import { handleFormSaveError } from '../common/forms/ValidationError';
import CustomCircularProgress from '../common/CustomCircularProgress';
import { NotAuthorizedPage } from '../status/StatusCodes';
import FormErrorResponseDisplay from '../common/forms/FormErrorResponseDisplay';
import { DatePicker } from '../common/forms/DatePicker';
import { Alert, ModalForm } from '../common/forms/ModalForm';
import { Can } from '../Can';
import { Modal } from '../common/Modal';
import { TipAlert } from '../common/TipAlert';
import InvoiceSlideout from './InvoiceSlideout';
import { CreditReasons } from './Invoice';

const buildEmailSubject = ({
    customerName,
    invoiceNumber,
    contractDescription,
    workRequestNumber,
    purchaseOrderNumber,
    tccCode,
    invoiceDate,
    dispatchOfcName,
    invoicingEmailSubject,
    useCTRReport,
    operationCenterCode,
    operationsCenter,
    printedInvoiceNumber,
}) => {
    const name = String(customerName ?? '');
    const custom = String(invoicingEmailSubject ?? '').trim();
    const present = (v) => v !== null && v !== undefined && `${v}`.trim() !== '';
    const joinNonEmpty = (...vals) => vals.filter(present).join(' ');

    if (custom) {
        return custom;
    }

    if (useCTRReport && operationCenterCode && operationCenterCode.length > 0 && printedInvoiceNumber) {
        const dt = moment(invoiceDate).format('MMDDYYYY');
        const p = operationsCenter.indexOf(' - ');
        const opsCenter = operationsCenter.substring(p + 3);
        return `${operationCenterCode} WD Wright Contracting ${dt} ${printedInvoiceNumber} ${opsCenter} - Flagging`;
    }

    // exact match cases that are truly exact
    if (name === 'Castine, Inc - Balt- DC' || name === 'Castine, Inc.') {
        return `Invoice ${invoiceNumber}${contractDescription ? ` - ${contractDescription}` : ''}`;
    }

    const isColumbiaGas = /\bcolumbia\s+gas\b/i.test(name);
    const isVeteranPower = /\bveteran\s+power\b/i.test(name);

    if (isVeteranPower) {
        return joinNonEmpty(purchaseOrderNumber, 'Invoice', invoiceNumber);
    }
    if (customerName === 'PINNACLE POWER SERVICES') {
        return joinNonEmpty(invoiceNumber, moment(invoiceDate).format('MM.DD.YY'), workRequestNumber, purchaseOrderNumber);
    }
    if (isColumbiaGas) {
        return `Invoice ${invoiceNumber}${present(tccCode) ? ` TCC ${tccCode}` : ''}`;
    }

    return `${customerName}${present(dispatchOfcName) ? ` ${dispatchOfcName}` : ''} invoices for ${invoiceDate}`;
};

class InvoiceIndex extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.invoiceSlideoutRef = React.createRef();
        this.invoiceIssuesSlideoutRef = React.createRef();

        const yesNo = [
            { label: 'Yes', value: 'true' },
            { label: 'No', value: 'false' },
        ];

        const path = props.location.pathname;
        const isRental = path.includes('rentalInvoice');

        this.state = {
            loading: true,
            rowData: [],
            rowsSelected: [],
            invoiceSlideoutOpen: false,
            selectedDetails: [],
            loadingReport: false,
            yesNo,
            isApproving: false,
            isDownloading: false,
            isDownloadingByContract: false,
            isDownloadingWorkCards: false,
            isEmailingByContract: false,
            errorResponse: {},
            disableExport: true,
            disableClearExport: true,
            disableCMDownload: true,
            disableDownloadByCustomer: true,
            clearExportItems: new Set(),
            showInvoiceIssuesSlideout: false,
            invoiceIssueMessages: [],
            timesheetNumbers: '',
            disableClear: true,
            isSyncing: false,
            invoiceNetSuiteLogOpen: false,
            invoiceNetSuiteLogLoading: false,
            willSyncInvoices: false,
            invoicesToBeSynced: [],
            netsuiteLogs: [],
            rentalInvoices: isRental,
            rentalProjects: [],
            showInvoiceModal: false,
            invoicing: false,
            billingDates: [],
            selectedProject: null,
            billingDate: null,
            baseUrl: null,
            invoiceCreditMemoOpen: false,
            creditMemoDate: new Date(),
        };

        this.onRowClicked = this.onRowClicked.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onRowSelected = this.onRowSelected.bind(this);
        this.handleSaveError = this.handleSaveError.bind(this);
    }

    componentDidMount() {
        this.populateState();
    }

    componentDidUpdate(prevProps, prevState) {
        const searchChanged = prevProps.location?.search !== this.props.location?.search;
        const finishedLoading = prevState.loading && !this.state.loading;
        if (searchChanged || finishedLoading) {
            this.openInvoiceFromQueryParam();
        }
    }

    openInvoiceFromQueryParam = () => {
        if (this.state.loading || !this.invoiceSlideoutRef.current) return;
        const params = new URLSearchParams(this.props.location?.search ?? '');
        const iId = params.get('iId');
        if (iId) {
            this.openInvoiceSlideout(iId);
        }
    };

    onAcceptInvoiceIssueReply = async (invoiceId, timesheetId, message) => {
        const model = {
            invoiceId,
            timesheetId,
            notes: message,
        };

        await httpClient.post(ApiRoutes.billingIssues.invoiceAccept(), model);

        toasty.success('Invoice reply accepted');

        this.setState({
            showInvoiceIssuesSlideout: false,
        });

        this.state.gridOptions.refresh();
    };

    onChange = onFieldChange;

    onDownloadCTR = async (data) => {
        const printedInvoiceNumber = await httpClient.js(ApiRoutes.invoice.printedInvoiceNumber(data.id))
            .catch(this.handleSaveError);

        if (!printedInvoiceNumber) return;
        this.setState({ isDownloadingCTR: true });
        if (data.id) {
            await httpClient
                .downloadFile(
                    ApiRoutes.invoice.ctrexport(data.id),
                    null,
                    `CTR Spreadsheet ${printedInvoiceNumber}.xlsx`,
                )
                .catch(this.handleSaveError);
        }
        this.setState({ isDownloadingCTR: false });
    };

    onDownloadInvoice = async (data) => {
        if (data.id) {
            const printedInvoiceNumber = await httpClient.js(ApiRoutes.invoice.printedInvoiceNumber(data.id))
                .catch(this.handleSaveError);

            if (!printedInvoiceNumber) return;
            this.setState({ isDownloading: true });
            await httpClient
                .downloadFile(
                    ApiRoutes.invoice.downloadInvoice(data.id),
                    null,
                    `Invoice ${printedInvoiceNumber}.zip`,
                )
                .catch(this.handleSaveError);

            this.setState({ isDownloading: false });

            this.state.gridOptions.refresh();
        }
    };

    onGetCMFiles = async () => {
        this.setState({ isDownloading: true });
        const { rowsSelected } = this.state;
        if (rowsSelected) {
            const model = rowsSelected.map((x) => x.id); //Invoice Id
            try {
                const response = await httpClient.post(
                    ApiRoutes.invoice.getCMExportFiles(),
                    model,
                    //This is required for the error to be brought back clean.
                    //If you don't specify this, the error handling does not work
                    httpClient.format.none,
                );
                if (response.ok) {
                    const filename = fileNameFromContentDisposition(
                        response.headers.get('content-disposition'),
                    );
                    const blob = await response.blob();
                    const a = document.createElement('a');
                    a.href = URL.createObjectURL(blob);
                    a.target = '_blank';
                    a.download = filename;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                } else {
                    if (response.status === 400) {
                        const serviceResponse = await response.json();
                        this.displaySaveErrors(serviceResponse);
                    } else {
                        const serviceResponse = {
                            title: 'Server Error',
                            errors: {
                                Exception: [await response.text()],
                            },
                        };
                        this.displaySaveErrors(serviceResponse);
                    }
                }
            } catch (ex) {
                toasty.error(ex);
            } finally {
                this.state.gridOptions.refresh();
                this.setState({ isDownloading: false });
            }
        }

        this.state.gridOptions.refresh();
        this.setState({ isDownloading: false });
    };

    onDownloadInvoices = async () => {
        this.setState({ isDownloadingByContract: true });

        const { rowsSelected } = this.state;
        if (rowsSelected) {
            const [{ invoiceCollationId }] = rowsSelected;
            const model = rowsSelected.map((x) => x.id); // Invoice Id
            try {
                const response = await httpClient.post(
                    ApiRoutes.invoice.downloadInvoices(),
                    model,
                    // This is required for the error to be brought back clean.
                    // If you don't specify this, the error handling does not work
                    httpClient.format.none,
                );

                if ((invoiceCollationId === 2 || (invoiceCollationId == 1 && rowsSelected.length === 1))) {
                    const qsWithAttachments = rowsSelected.filter((x) => x.attachments?.length > 0);
                    if (qsWithAttachments?.length > 0) {
                        qsWithAttachments.flatMap((qs) => qs.attachments).forEach((attachment) => {
                            const url = ApiRoutes.fileUpload.getAttachmentById(attachment.id);
                            httpClient.open(url, '_blank');
                        });
                    }
                }

                if (response.ok) {
                    let filename = fileNameFromContentDisposition(
                        response.headers.get('content-disposition'),
                    );
                    filename = filename.replaceAll('_', ' ');

                    const blob = await response.blob();
                    const a = document.createElement('a');
                    a.href = URL.createObjectURL(blob);
                    a.target = '_blank';
                    a.download = filename;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                } else if (response.status === 400) {
                    const serviceResponse = await response.json();
                    this.displaySaveErrors(serviceResponse);
                } else {
                    const serviceResponse = {
                        title: 'Server Error',
                        errors: {
                            Exception: [await response.text()],
                        },
                    };
                    this.displaySaveErrors(serviceResponse);
                }
            } catch (ex) {
                toasty.error(ex);
            } finally {
                this.state.gridOptions.refresh();
                this.setState({ isDownloadingByContract: false });
            }
        }

        this.state.gridOptions.refresh();
        this.setState({ isDownloading: false });
    };

    onDownloadTimesheets = async (data) => {
        this.setState({ isDownloadingTimesheets: true });
        if (data.id) {
            const printedInvoiceNumber = await httpClient.js(ApiRoutes.invoice.printedInvoiceNumber(data.id)).catch(this.handleSaveError);
            if (!printedInvoiceNumber) return;

            let timesheetsFileName = `Timesheets ${printedInvoiceNumber}`;
            if (
                data.invoiceTimesheetUsesWO
                && data.tcNumber != null
                && data.tcNumber.length > 0
            ) {
                timesheetsFileName = data.tcNumber;
            }

            await httpClient
                .downloadFile(
                    ApiRoutes.invoice.downloadInvoiceTimesheets(data.id),
                    null,
                    `${timesheetsFileName}.pdf`,
                )
                .catch(this.handleSaveError);
        }
        this.setState({ isDownloadingTimesheets: false });
    };

    generateInvoiceEmail = async (rowsSelected) => {
        // --- helpers ---
        const safeHeader = (s) => String(s ?? '').replace(/[\r\n]/g, ' ').trim(); // prevent header injection
        const EOL = '\n';
        const boundary = 'boundary_text_string';
        const partStart = `--${boundary}${EOL}`;
        const partEnd = `--${boundary}--${EOL}`;

        let emlContent = '';
        let dispatchOfcName = '';

        const [{
            id,
            dispatchOfficeLocationName,
            customerName,
            invoiceDate,
            invoiceNumber,
            contractDescription,
            workRequestNumber,
            purchaseOrderNumber,
            tccCode,
            invoicingEmailSubject,
            invoiceCollationId,
            billingContactEmail,
            useCTRReport,
            operationCenterCode,
            operationsCenter,
        }] = rowsSelected;

        const { baseUrl } = { ...this.state };
        const emailTo = billingContactEmail ?? '';
        const data = rowsSelected.map((x) => x.id);
        const invoices = Array.from(data).join(',');

        // include dispatch office?
        const response = await httpClient.js(ApiRoutes.invoice.includeDispatchOfficeName(id))
            .catch(this.handleSaveError);

        if (response) {
            dispatchOfcName = dispatchOfficeLocationName;
        }

        let subjectPrintedInvoiceNumber = null;

        // Support for Duke invoice printing
        if (useCTRReport && operationCenterCode && rowsSelected.length === 1) {
            subjectPrintedInvoiceNumber = await httpClient.js(ApiRoutes.invoice.printedInvoiceNumber(id))
                .catch(this.handleSaveError);
        }

        const emailSubject = buildEmailSubject({
            customerName,
            invoiceNumber,
            contractDescription,
            workRequestNumber,
            purchaseOrderNumber,
            tccCode,
            invoiceDate,
            dispatchOfcName,
            invoicingEmailSubject,
            useCTRReport,
            operationCenterCode,
            operationsCenter,
            printedInvoiceNumber: subjectPrintedInvoiceNumber,
        });

        emlContent += `To: ${safeHeader(emailTo)}${EOL}`;
        emlContent += `Subject: ${safeHeader(emailSubject)}${EOL}`;
        emlContent += `X-Unsent: 1${EOL}`;
        emlContent += `Content-Type: multipart/mixed; boundary=${boundary}${EOL}${EOL}`;
        const printedInvoiceNumbers = new Map();

        await Promise.all(rowsSelected.map(async (r) => {
            const printedInvoiceNumber = await httpClient.js(ApiRoutes.invoice.printedInvoiceNumber(r.id));
            printedInvoiceNumbers.set(r.id, printedInvoiceNumber);
        }));

        let attachmentsText = '<br><br><b>Additional files:</b>';
        let hasAttachments = false;
        rowsSelected.forEach((r) => {
            if (r.attachments?.length) {
                hasAttachments = true;
                const printNum = printedInvoiceNumbers.get(r.id);
                attachmentsText += `<br>Attachments for Invoice ${printNum}:`;
                r.attachments.forEach((a) => {
                    const attachmentUrl = `${baseUrl}${ApiRoutes.public.getAttachmentById(a.id)}`;
                    attachmentsText += `<br><a href='${attachmentUrl}'>${a.name}</a>.`;
                });
            }
        });

        const htmlCommon = `<html>Attached are ${customerName} invoices and timesheets for ${invoiceDate}
            ${hasAttachments ? attachmentsText : ''}
            </html>`;

        if (invoiceCollationId === 0) {
            // No collation - separate files
            emlContent += partStart;
            emlContent += `Content-Type: text/html${EOL}${EOL}`;
            emlContent += `${htmlCommon}${EOL}${EOL}`;

            this.setState({ isEmailingByContract: true });

            for (let i = 0; i < rowsSelected.length; i += 1) {
                let CTRFile = null;
                let ProofOf40File = null;

                let timesheetsFile = null;
                if (rowsSelected[i].isQuickSale !== true && rowsSelected[i].hasNonRentalTimesheets) {
                    timesheetsFile = await httpClient
                        .js(
                            ApiRoutes.invoice.streamInvoiceTimesheets(
                                rowsSelected[i].id,
                            ),
                        )
                        .catch(this.handleSaveError);
                }

                const [invoiceFile, printedInvoiceNumber] = await Promise.all([
                    httpClient.js(ApiRoutes.invoice.streamInvoice(rowsSelected[i].id)),
                    printedInvoiceNumbers.get(rowsSelected[i].id),
                ]).catch(this.handleSaveError);

                if (!printedInvoiceNumber) return;

                if (useCTRReport) {
                    CTRFile = await httpClient
                        .js(ApiRoutes.invoice.streamCTRFile(rowsSelected[i].id))
                        .catch(this.handleSaveError);
                }

                if (rowsSelected[i].useProofOf40 && rowsSelected[i].hasOvertime) {
                    ProofOf40File = await httpClient.js(ApiRoutes.invoice.streamProofOf40File(rowsSelected[i].id)).catch(this.handleSaveError);
                }

                let timesheetsFileName = `Timesheets ${printedInvoiceNumber}`;
                if (
                    rowsSelected[i].invoiceTimesheetUsesWO &&
                    rowsSelected[i].tcNumber != null &&
                    rowsSelected[i].tcNumber.length > 0
                ) {
                    timesheetsFileName = rowsSelected[i].tcNumber;
                }

                // Invoice PDF
                emlContent += partStart;
                emlContent += `Content-Type: application/pdf; name="Invoice ${printedInvoiceNumber}${dispatchOfcName ? ` ${dispatchOfcName}` : ''}.pdf"${EOL}`;
                emlContent += `Content-Transfer-Encoding: base64${EOL}`;
                emlContent += `Content-Disposition: attachment${EOL}${EOL}`;
                emlContent += `${invoiceFile.data}${EOL}${EOL}`;

                // Timesheets PDF
                if (timesheetsFile) {
                    emlContent += partStart;
                    emlContent += `Content-Type: application/pdf; name="${timesheetsFileName}.pdf"${EOL}`;
                    emlContent += `Content-Transfer-Encoding: base64${EOL}`;
                    emlContent += `Content-Disposition: attachment${EOL}${EOL}`;
                    emlContent += `${timesheetsFile.data}${EOL}${EOL}`;
                }

                // Optional CTR
                if (useCTRReport && CTRFile != null) {
                    emlContent += partStart;
                    emlContent += `Content-Type: application/vnd.openxmlformats-officedocument.spreadsheetml.sheet; name="CTR ${printedInvoiceNumber}.xlsx"${EOL}`;
                    emlContent += `Content-Transfer-Encoding: base64${EOL}`;
                    emlContent += `Content-Disposition: attachment${EOL}${EOL}`;
                    emlContent += `${CTRFile.data}${EOL}${EOL}`;
                }

                // Optional ProofOf40
                if (rowsSelected[i].useProofOf40 && ProofOf40File != null) {
                    emlContent += partStart;
                    emlContent += `Content-Type: application/vnd.openxmlformats-officedocument.spreadsheetml.sheet; name=ProofOf40.xlsx${EOL}`;
                    emlContent += `Content-Transfer-Encoding: base64${EOL}`;
                    emlContent += `Content-Disposition: attachment${EOL}${EOL}`;
                    emlContent += `${ProofOf40File.data}${EOL}${EOL}`;
                }
            }

            const workCardsInvoiceIds = rowsSelected?.filter((r) => r.useEmbedWorkCard)?.map((i) => i.id)?.join(',');

            if (workCardsInvoiceIds?.length) {
                const workCardsPdf = await httpClient
                    .js(ApiRoutes.invoice.streamInvoiceWorkCards(workCardsInvoiceIds))
                    .catch(this.handleSaveError);

                if (workCardsPdf) {
                    emlContent += partStart;
                    emlContent += `Content-Type: application/pdf; name="backup.pdf"${EOL}`;
                    emlContent += `Content-Transfer-Encoding: base64${EOL}`;
                    emlContent += `Content-Disposition: attachment${EOL}${EOL}`;
                    emlContent += `${workCardsPdf.data}${EOL}${EOL}`;
                }
            }

            emlContent += partEnd;
            this.setState({ isEmailingByContract: false });
        } else if (invoiceCollationId === 1) {
            // Collated - separate files per row (streamInvoices)
            emlContent += partStart;
            emlContent += `Content-Type: text/html${EOL}${EOL}`;
            emlContent += `${htmlCommon}${EOL}${EOL}`;

            this.setState({ isEmailingByContract: true });

            for (let i = 0; i < rowsSelected.length; i += 1) {
                let CTRFile = null;
                let ProofOf40File = null;

                const [invoiceFile, printedInvoiceNumber] = await Promise.all([
                    httpClient.js(ApiRoutes.invoice.streamInvoices(rowsSelected[i].id)),
                    httpClient.js(ApiRoutes.invoice.printedInvoiceNumber(rowsSelected[i].id)),
                ]).catch(this.handleSaveError);

                if (!printedInvoiceNumber) return;

                if (useCTRReport) {
                    CTRFile = await httpClient
                        .js(ApiRoutes.invoice.streamCTRFile(rowsSelected[i].id))
                        .catch(this.handleSaveError);
                }

                if (rowsSelected[i].useProofOf40 && rowsSelected[i].hasOvertime) {
                    ProofOf40File = await httpClient.js(ApiRoutes.invoice.streamProofOf40File(rowsSelected[i].id)).catch(this.handleSaveError);
                }

                // Collated invoice PDF
                emlContent += partStart;
                emlContent += `Content-Type: application/pdf; name="Invoice ${printedInvoiceNumber}${dispatchOfcName ? ` ${dispatchOfcName}` : ''}.pdf"${EOL}`;
                emlContent += `Content-Transfer-Encoding: base64${EOL}`;
                emlContent += `Content-Disposition: attachment${EOL}${EOL}`;
                emlContent += `${invoiceFile.data}${EOL}${EOL}`;

                // Optional CTR
                if (useCTRReport && CTRFile != null) {
                    emlContent += partStart;
                    emlContent += `Content-Type: application/vnd.openxmlformats-officedocument.spreadsheetml.sheet; name="CTR ${printedInvoiceNumber}.xlsx"${EOL}`;
                    emlContent += `Content-Transfer-Encoding: base64${EOL}`;
                    emlContent += `Content-Disposition: attachment${EOL}${EOL}`;
                    emlContent += `${CTRFile.data}${EOL}${EOL}`;
                }

                // Optional ProofOf40
                if (rowsSelected[i].useProofOf40 && ProofOf40File != null) {
                    emlContent += partStart;
                    emlContent += `Content-Type: application/vnd.openxmlformats-officedocument.spreadsheetml.sheet; name=ProofOf40.xlsx${EOL}`;
                    emlContent += `Content-Transfer-Encoding: base64${EOL}`;
                    emlContent += `Content-Disposition: attachment${EOL}${EOL}`;
                    emlContent += `${ProofOf40File.data}${EOL}${EOL}`;
                }
            }

            emlContent += partEnd;
            this.setState({ isEmailingByContract: false });
        } else {
            // Collated into a single downloadable file (pdf/zip)
            this.setState({ isEmailingByContract: true });

            const downloadFile = await httpClient
                .js(ApiRoutes.invoice.streamInvoices(invoices))
                .catch(this.handleSaveError);

            const contentType = downloadFile.fileName.includes('.pdf')
                ? 'application/pdf'
                : 'application/zip';

            emlContent += partStart;
            emlContent += `Content-Type: text/html${EOL}${EOL}`;
            emlContent += `${htmlCommon}${EOL}${EOL}`;

            emlContent += partStart;
            emlContent += `Content-Type: ${contentType}; name="${downloadFile.fileName}"${EOL}`;
            emlContent += `Content-Transfer-Encoding: base64${EOL}`;
            emlContent += `Content-Disposition: attachment${EOL}${EOL}`;
            emlContent += `${downloadFile.data}${EOL}${EOL}`;

            emlContent += partEnd;
            this.setState({ isEmailingByContract: false });
        }

        return emlContent;
    };

    onEmailButtonClick = async () => {
        const { rowsSelected } = this.state;
        if (rowsSelected.length == 0) return;

        const emlContent = await this.generateInvoiceEmail(rowsSelected);

        const href = 'data:message/rfc822 eml;charset=utf-8,' + encodeURIComponent(emlContent);

        const obj = document.createElement('a');
        obj.appendChild(document.createTextNode('fileLink'));
        obj.href = href;
        obj.id = 'fileLink';
        obj.download = 'invoices.eml';
        obj.style = 'display:none;';
        document.body.appendChild(obj);
        document.getElementById('fileLink').click();
        document.body.removeChild(obj);

        this.state.gridOptions.refresh();
    };

    onEmailRowClick = async (data) => {
        const { rowsSelected } = this.state;
        if (rowsSelected.length == 0) return;

        const emlContent = await this.generateInvoiceEmail([data]);

        const href = 'data:message/rfc822 eml;charset=utf-8,' + encodeURIComponent(emlContent);
        const obj = document.createElement('a');
        const linkText = document.createTextNode('fileLink');
        obj.appendChild(linkText);
        obj.href = href;
        obj.id = 'fileLink';
        obj.download = 'invoice.eml';
        obj.style = 'display:none;';
        document.body.appendChild(obj);
        document.getElementById('fileLink').click();
        document.body.removeChild(obj);

        this.state.gridOptions.refresh();
    };

    onExportToNetSuite = async () => {
        this.setState({ isDownloading: true });
        const { rowsSelected } = this.state;
        if (rowsSelected) {
            const model = rowsSelected.map((x) => x.id); // Invoice Id
            try {
                const response = await httpClient.post(
                    ApiRoutes.invoice.exportToNetSuite(),
                    model,
                    // This is required for the error to be brought back clean.
                    // If you don't specify this, the error handling does not work
                    httpClient.format.none,
                );

                if (response.ok) {
                    const filename = fileNameFromContentDisposition(
                        response.headers.get('content-disposition'),
                    );
                    const blob = await response.blob();
                    const a = document.createElement('a');
                    a.href = URL.createObjectURL(blob);
                    a.target = '_blank';
                    a.download = filename;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                } else if (response.status === 400 || response.status === 500) {
                    const serviceResponse = await response.json();
                    toasty.error(serviceResponse.message);
                } else {
                    const serviceResponse = {
                        title: 'Server Error',
                        errors: {
                            Exception: [await response.text()],
                        },
                    };
                    this.displaySaveErrors(serviceResponse);
                }
            } catch (ex) {
                toasty.error(ex);
            } finally {
                this.state.gridOptions.refresh();
                this.setState({ isDownloading: false });
            }
        }

        this.state.gridOptions.refresh();
        this.setState({ isDownloading: false });
    };

    onGetExportFile = async (id, type) => {
        this.setState({ isDownloading: true });

        try {
            const response = await httpClient.get(
                ApiRoutes.invoice.getExportFile(id, type),
                httpClient.format.none,
            );

            if (response.ok) {
                const filename = fileNameFromContentDisposition(
                    response.headers.get('content-disposition'),
                );
                const blob = await response.blob();
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.target = '_blank';
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                a.remove();
                this.state.gridOptions.refresh();
            } else if (response.status === 400) {
                const serviceResponse = await response.json();
                this.displaySaveErrors(serviceResponse);
            } else {
                const serviceResponse = {
                    title: 'Server Error',
                    errors: {
                        Exception: [await response.text()],
                    },
                };
                this.displaySaveErrors(serviceResponse);
            }
        } catch (error) {
            toasty.error(error);
        }

        this.setState({ isDownloading: false });
    };

    syncInvoices = async () => {
        const { rowsSelected } = this.state;
        if (rowsSelected && rowsSelected.length > 0) {
            try {
                this.setState({
                    invoicesToBeSynced: rowsSelected.map((x) => ({ id: x.id, invoiceNumber: x.invoiceNumber, status: 'todo' })),
                    willSyncInvoices: true,
                    isSyncing: true,
                });
                const syncPromises = rowsSelected.map((invoice) => this.syncInvoiceToNetSuite(invoice));
                await Promise.all(syncPromises);
                this.setState({
                    isSyncing: false,
                });
                this.state.gridOptions.refresh();
            } catch (ex) {
                toasty.error(ex);
                this.state.gridOptions.refresh();
            }
        }
    };


    syncInvoiceToNetSuite = async (data, rowIndex = null) => {
        if (data.id) {
            const rowNode = this.state.gridApi.getRowNode(String(rowIndex));
            if (rowIndex == null) {
                this.setState((prevState) => {
                    const updatedInvoices = prevState.invoicesToBeSynced.map((invoice) => {
                        if (invoice.id === data.id) {
                            return { ...invoice, status: 'loading' };
                        }
                        return invoice;
                    });
                    return { invoicesToBeSynced: updatedInvoices };
                });
            } else {
                data.isSyncing = true;
                rowNode.setData(data);
                this.state.gridApi.refreshCells();
            }

            try {
                const response = await httpClient.post(
                    ApiRoutes.invoice.syncInvoicesToNetSuite(),
                    data.id,
                    httpClient.format.none,
                );
                data.isSyncing = false;
                if (response.ok) {
                    if (rowIndex == null) {
                        this.setState((prevState) => {
                            const updatedInvoices = prevState.invoicesToBeSynced.map((invoice) => {
                                if (invoice.id === data.id) {
                                    return { ...invoice, status: 'done' };
                                }
                                return invoice;
                            });
                            return { invoicesToBeSynced: updatedInvoices };
                        });
                    } else {
                        this.state.gridOptions.refresh();
                    }
                } else {
                    let errorMessage = '';
                    if (response.status === 400) {
                        const serviceResponse = await response.json();
                        errorMessage = serviceResponse?.message;
                    } else {
                        const responseJson = await response.json();
                        errorMessage = responseJson.message;
                    }
                    if (rowIndex == null) {
                        this.setState((prevState) => {
                            const updatedInvoices = prevState.invoicesToBeSynced.map((invoice) => {
                                if (invoice.id === data.id) {
                                    return { ...invoice, status: 'error', error: errorMessage };
                                }
                                return invoice;
                            });
                            return { invoicesToBeSynced: updatedInvoices };
                        });
                    } else {
                        const serviceResponse = {
                            title: 'Server Error',
                            errors: {
                                Exception: [errorMessage],
                            },
                        };
                        this.displaySaveErrors(serviceResponse);
                        rowNode.setData(data);
                        this.state.gridApi.refreshCells();
                    }
                }
            } catch (ex) {
                toasty.error(ex);
            }
        }
    };

    syncInvoicesCMToNetSuite = async ({
        creditMemoDate, reason, memo, invoiceCreditMemoTypeId,
    }) => {
        const { rowsSelected } = this.state;
        if (rowsSelected) {
            try {
                this.setState({
                    isSyncing: true,
                });
                const model = {
                    creditMemoDate: moment(creditMemoDate ?? new Date()).format('YYYY-MM-DD'),
                    reason,
                    memo,
                    invoiceIds: rowsSelected.map((x) => x.id),
                    invoiceCreditMemoTypeId,
                };
                const response = await httpClient.post(
                    ApiRoutes.invoice.syncInvoicesCMToNetSuite(),
                    model,
                    httpClient.format.none,
                );

                this.setState({
                    isSyncing: false,
                    invoiceCreditMemoOpen: false,
                    creditMemoDate: new Date(),
                });
                if (response.ok) {
                    this.state.gridOptions.refresh();
                } else {
                    if (response.status === 400) {
                        const serviceResponse = await response.json();
                        this.displaySaveErrors(serviceResponse?.message);
                    } else {
                        const responseJson = await response.json();
                        const serviceResponse = {
                            title: 'Server Error',
                            errors: {
                                Exception: [responseJson.message],
                            },
                        };
                        this.displaySaveErrors(serviceResponse);
                        this.state.gridOptions.refresh();
                    }
                }
            } catch (ex) {
                toasty.error(ex);
                this.state.gridOptions.refresh();
            }
        }
    };

    openInvoiceNetSuiteLogs = async (data) => {
        if (data.id) {
            this.setState({
                invoiceNetSuiteLogOpen: true,
                invoiceNetSuiteLogLoading: true,
            });
            try {
                const response = await httpClient.get(
                    ApiRoutes.invoice.getInvoiceNetSuiteReport(data.id),
                    httpClient.format.none,
                );
                this.setState({
                    invoiceNetSuiteLogLoading: false,
                });
                if (response.ok) {
                    const list = await response.json();
                    this.setState({
                        netsuiteLogs: list,
                    });
                    if (list?.length > 0) {
                        this.state.gridOptions.refresh();
                    }
                } else {
                    if (response.status === 400) {
                        const serviceResponse = await response.json();
                        this.displaySaveErrors(serviceResponse?.message);
                    } else {
                        const responseJson = await response.json();
                        const serviceResponse = {
                            title: 'Server Error',
                            errors: {
                                Exception: [responseJson.message],
                            },
                        };
                        this.displaySaveErrors(serviceResponse);
                    }
                }
            } catch (ex) {
                toasty.error(ex);
            }
        }
    };

    handleCloseNetSuiteLog = () => {
        this.setState({
            invoiceNetSuiteLogOpen: false,
            netsuiteLogs: [],
        });
    };

    onInvoiceIssuesSlideout = async (row) => {
        const invoice = await httpClient.js(ApiRoutes.invoice.byId(row.id));

        const messages = await httpClient.post(
            ApiRoutes.billingIssues.invoiceMessages(),
            row.id,
        );

        this.setState({
            invoiceIssueMessages: messages, timesheetNumbers: row.timesheetNumber, selectedInvoice: invoice, showInvoiceIssuesSlideout: true,
        });
    };

    onInvoiceSlideoutClosed = async () => {
        await this.context.setFormOpened(false);
        this.setState({ rowsSelected: [], isInvoicing: false });
        this.state.gridOptions.refresh();
    };

    onInvoiceNetSuiteLogClosed = async () => {
        await this.context.setFormOpened(false);
        this.setState({ rowsSelected: [], isInvoicing: false });
    };

    onOver40Download = async (data) => {
        this.setState({ isDownloadingOver40: true });
        if (data.id) {
            const printedInvoiceNumber = await httpClient.js(ApiRoutes.invoice.printedInvoiceNumber(data.id))
                .catch(this.handleSaveError);

            if (!printedInvoiceNumber) return;

            await httpClient
                .downloadFile(
                    ApiRoutes.invoice.proofof40(data.id),
                    null,
                    `Proof_Of_40_${printedInvoiceNumber}.xlsx`,
                )
                .catch(this.handleSaveError);
        }
        this.setState({ isDownloadingOver40: false });
    };

    onPrintInvoice = (id) => {
        if (id) {
            this.setState({ printing: true });

            const route = ApiRoutes.report.invoice(id);

            this.setState({ printing: false });
            httpClient.open(route, '_self');
        }
    };

    onRejectToInvoiceIssues = async (invoiceId, timesheetId, message, updateStatus) => {
        if (message == null || message.length == 0) {
            toasty.error('Please supply rejection notes');
            return;
        }

        const model = {
            invoiceId,
            timesheetId,
            notes: message,
            updateStatus,
        };

        await httpClient.post(ApiRoutes.billingIssues.invoiceReject(), model);

        toasty.success('Invoice rejected to Billing Issues');

        this.setState({
            showInvoiceIssuesSlideout: false,
        });

        this.state.gridOptions.refresh();
    };

    onRowClicked = (event) => {
        const selection = event.api.getSelectedRows();
        const row = selection.length ? selection[0] : null;

        if (row) {
            this.setState({ selectedRow: row });
        }
    };

    // https://stackoverflow.com/questions/44263350/count-number-of-selected-rows-in-ag-grid
    // Possibly use lodash dequeue?
    onRowSelected(e) {
        const rs = e.api.getSelectedRows();

        let canExport = rs.every((i) => i.invoiceStatus === 'Approved' && (i.isRentalInvoice || i.invoiceEmailDate || i.invoiceDownloadDate));
        let canClear = rs.every((i) => i.invoiceStatus === 'Exported');

        const hasCreditMemo = rs.some((i) => i.netSuiteCMExportNumber && !i.netSuiteCMExportNumber.includes('EXP-'));

        const exportNumber = rs[0]?.netSuiteExportNumber ?? '';
        let canClearExport = rs.every((i) => (
            i.invoiceStatus === 'Exported'
            && i.netSuiteExportNumber === exportNumber
        ));

        const customerName = rs[0]?.customerName ?? '';
        const canExportCustomer = rs.every((i) => (
            i.customerName === customerName
            && (i.invoiceStatus === 'Approved'
                || i.invoiceStatus === 'Exported')
        ));

        if (rs.length == 0) {
            canExport = false;
            canClear = false;
            canClearExport = false;
        }

        this.setState({
            rowsSelected: rs,
            disableExport: !canExport,
            disableClearExport: !canClearExport,
            disableClear: !canClear,
            disableDownloadByCustomer: !canExportCustomer,
            disableCMDownload: !hasCreditMemo,
        });
    }

    clearSaveErrors = () => this.setState({ errorResponse: {} });

    displaySaveErrors = (response) => this.setState({ errorResponse: response });

    handleCheckboxChange = (itemKey) => {
        const newClearExportItems = new Set(this.state.clearExportItems);

        if (!newClearExportItems.has(itemKey)) {
            newClearExportItems.add(itemKey);
        } else {
            newClearExportItems.delete(itemKey);
        }

        this.setState({ clearExportItems: newClearExportItems });
    };

    handleSaveError = (err) => handleFormSaveError(this, err);

    openInvoiceSlideout = async (id) => {
        if (id) {
            const model = {
                billingIds: null,
                invoiceId: id,
            };

            await this.context.setFormOpened(true);
            this.invoiceSlideoutRef.current.open(model);
        }
    };

    canSyncToNetSuite = () => {
        if (!this.context || !this.context.checkUserHasPermission) {
            return false;
        }

        return this.context.checkUserHasPermission(ApplicationPermissions.quickBooksInventoryItem_edit);
    };

    toggleInvoiceModal = async () => {
        const { showInvoiceModal } = this.state;
        const rentalProjects = await httpClient.js(ApiRoutes.typeAheads.rentalProjects());

        this.setState({
            showInvoiceModal: !showInvoiceModal,
            rentalProjects,
            selectedProject: null,
            billingDate: null,
            billingDates: [],
        });
    };

    onProjectSelected = (project) => {
        const dates = [];

        if (project != null) {
            const lastEndDate = project.closedOn ? moment(project.closedOn) : moment();

            if (project?.billingCycleId == BillingCycles.Daily) {
                let billingDate = project?.lastInvoiced ?
                    moment(project?.lastInvoiced).add(2, 'day').toDate() :
                    moment(project.billingStartDate).add(1, 'day').toDate();

                while (moment(billingDate).startOf('day') <= lastEndDate.startOf('day')) {
                    dates.push(billingDate);
                    billingDate = moment(billingDate).add(1, 'day').toDate();
                }
            } else {
                let billingDate = project.billingCycleId == BillingCycles.Weekly ?
                    this.getNextWeeklyBillingDate(project, project.billingStartDate) :
                    this.getNextMonthlyBillingDate(project, project.billingStartDate);

                while (moment(billingDate) < lastEndDate) {
                    dates.push(billingDate);
                    billingDate = project.billingCycleId == BillingCycles.Weekly ?
                        this.getNextWeeklyBillingDate(project, billingDate) :
                        this.getNextMonthlyBillingDate(project, billingDate);
                }

                //Add the project close day, if not billed
                if (project.closedOn && (!project?.lastInvoiced || moment(project.closedOn) > moment(project?.lastInvoiced))) {
                    dates.push(moment(project.closedOn).add(1, 'day').toDate());
                }

                if (dates.length == 0 || (project?.lastInvoiced && !dates.find((d) => moment(d) > moment(project?.lastInvoiced).add(1, 'day')))) {
                    toasty.warning(project.lastInvoiced != null ? 'This project has already been fully billed.' : 'There is nothing to be billed yet.');
                }
            }
        }

        this.setState({
            selectedProject: project,
            billingDate: null,
            billingDates: dates,
        });
    };

    // eslint-disable-next-line
    getNextWeeklyBillingDate = (project, lastBillingDate) => {
        if (project.weeklyBillingCycleEndDay != null) {
            const nextMatch = moment(lastBillingDate).day(project.weeklyBillingCycleEndDay + 1);
            // If the calculated day is same or before the lastBillingDate, move to next week
            while (nextMatch.isSameOrBefore(moment(lastBillingDate), 'day') ||
                (!project.billFirstPartialPeriod && nextMatch.isBefore(moment(lastBillingDate).add(7, 'days'), 'day'))
            ) {
                nextMatch.add(1, 'week');
            }
            return nextMatch.toDate();
        }

        // Default: add 7 days
        return moment(lastBillingDate).add(7, 'days').toDate();
    };

    getMaxBillingDate = () => {
        const { selectedProject } = this.state;
        if (!selectedProject) {
            return null;
        }

        if (selectedProject.closedOn) {
            return moment(selectedProject.closedOn).add(1, 'day').toDate();
        }

        return new Date();
    };

    getNextMonthlyBillingDate = (project, lastBillingDate) => {
        const { tenantSettings } = this.context.tenant;

        if (project.monthlyBillingCycleEndDay != null) {
            const nextMatch = moment(lastBillingDate)
                .startOf('month')
                .date(project.monthlyBillingCycleEndDay + 1);

            // If the computed date is before or same as lastBillingDate, move to next month
            if (nextMatch.isSameOrBefore(lastBillingDate, 'day') ||
                (!project.billFirstPartialPeriod && nextMatch.isBefore(moment(lastBillingDate).add(30, 'days'), 'day'))) {
                nextMatch.add(1, 'month');
            }
            return nextMatch.toDate();
        }

        // Default fallback: add 30 days or the 1st of the month if Month End setting enabled
        return tenantSettings.enableMonthEndRentalInvoice ?
            moment(lastBillingDate).add(1, 'month').startOf('month').toDate() :
            moment(lastBillingDate).add(30, 'days').toDate();
    };

    onGenerateInvoice = async () => {
        try {
            this.setState({ invoicing: true });
            const { selectedProject, billingDate } = this.state;
            const apiRoute = selectedProject ? ApiRoutes.project.invoice(selectedProject.value) : ApiRoutes.invoice.createRentalInvoiceTask(this.context.tenant.id);
            const response = await httpClient.post(apiRoute, moment(billingDate).add(-1, 'day'), httpClient.format.none);

            const serviceResponse = await response.json();
            if (!response.ok) throw new Error(serviceResponse?.message || 'Failed to invoice project');

            toasty.success(
                selectedProject ? `Invoice ${serviceResponse} created successfully!` : 'Invoice scheduler created successfully!',
            );

            this.state.gridOptions.refresh();
        } catch (error) {
            toasty.error(
                error?.message || 'An error occurred while billing the project.',
            );
        } finally {
            this.setState({
                showInvoiceModal: false,
                invoicing: false,
            });
        }
    };

    canClearInvoices = () => {
        if (!this.context || !this.context.checkUserHasPermission) {
            return false;
        }

        return this.context.checkUserHasPermission(ApplicationPermissions.invoice_clear);
    };

    getInvoiceChargesReport = async () => {
        const { gridOptions, rentalInvoices } = this.state;
        this.setState({ isDownloading: true });
        try {
            const formatYMD = (date) => moment(date).format('YYYY-MM-DD');

            const req = getServerSideRowRequest(gridOptions);

            await httpClient.downloadFile(
                ApiRoutes.invoice.getInvoiceChargesReport(rentalInvoices ?? false),
                req,
                `Invoice Charges Report ${formatYMD(new Date())}.xlsx`,
            );
        } finally {
            this.setState({ isDownloading: false });
        }
    };

    onDownloadWorkCards = async (data) => {
        if (!data?.id || !data?.showWorkCardDownload) return;

        const printedInvoiceNumber = await httpClient
            .js(ApiRoutes.invoice.printedInvoiceNumber(data.id))
            .catch(this.handleSaveError);

        if (!printedInvoiceNumber) return;

        this.setState({ isDownloadingWorkCards: true });

        try {
            await httpClient.downloadFile(
                ApiRoutes.invoice.downloadInvoiceWorkCards(String(data.id)),
                null,
                `Work Cards ${printedInvoiceNumber}.pdf`,
            );
        } catch (err) {
            console.error(err);
            toasty.warning('No work cards are available for this invoice.');
        } finally {
            this.setState({ isDownloadingWorkCards: false });
        }
    };

    populateState = async () => {
        const { rentalInvoices } = this.state;

        const [invoiceStatuses, baseUrl, creditMemoTypeOptions] = await Promise.all([
            httpClient.js(ApiRoutes.typeAheads.invoiceStatuses()),
            httpClient.js(ApiRoutes.typeAheads.baseUrl()),
            httpClient.js(ApiRoutes.typeAheads.creditMemoTypeOptions()),
        ]);

        const context = this;
        const gridOptions = createGridOptions(this);

        // https://www.ag-grid.com/documentation/javascript/row-selection/#checkbox-selection
        // One column must have "checkboxSelection" set to true.
        // Note: headerCheckboxSelection not available with infinite scroll.
        gridOptions.rowSelection = 'multiple';

        gridOptions.postProcessData = this.transformRowData;
        gridOptions.onRowSelected = this.onRowSelected;
        gridOptions.getRowNodeId = (data) => data.id;
        gridOptions.rowClassRules = {
            'ag-row-warning': (params) => (params.data ?? {}).hasOverride === 'Yes',
            'ag-row-danger': (params) => (params.data ?? {}).isEmergency === true,
        };

        gridOptions.components = {
            selectFilter: DataGridSelectFilter,
            selectFloatingFilter: DataGridSelectFloatingFilter,
            nameRenderer: LinkCellRenderer,
            iconRenderer: IconCellRenderer,
            variableLinkRenderer: VariableLinkCellRenderer,
        };
        gridOptions.onRowClicked = this.onRowClicked;

        const invoiceStatusFilterParams = {
            suppressFilterButton: true,
            options: invoiceStatuses,
            optionsLabel: 'label',
            optionsValue: 'value',
        };
        const invoiceSyncStatusFilterParams = {
            suppressFilterButton: true,
            options: [{ label: 'Ready to be Synced', value: 'Ready to be Synced' }, { label: 'Synced', value: 'Synced' }],
            optionsLabel: 'label',
            optionsValue: 'value',
        };

        gridOptions.columnDefs = [
            {
                headerName: '',
                valueGetter: 'node.id',
                sortable: false,
                hide: true,
                flex: 1,
                maxWidth: 35,
                cellRenderer: indexCellRenderer,
            },
            {
                colId: 'InvoiceDate',
                headerName: 'Date',
                field: 'invoiceDate',
                sortable: true,
                flex: 1.5,
                sort: { direction: 'asc', priority: 0 },
                filter: 'agDateColumnFilter',
                filterParams: DateFilterDefaults,
            },
            {
                colId: 'AccountingDate',
                headerName: 'Accounting Date',
                field: 'accountingDate',
                sortable: true,
                flex: 1.5,
                sort: { direction: 'asc', priority: 0 },
                filter: 'agDateColumnFilter',
                filterParams: DateFilterDefaults,
            },
            {
                colId: 'invoiceEmailDate',
                headerName: 'Emailed On',
                field: 'invoiceEmailDate',
                sortable: true,
                flex: 1.5,
                sort: { direction: 'asc', priority: 0 },
                filter: 'agDateColumnFilter',
                filterParams: DateFilterDefaults,
            },
            {
                colId: 'invoiceNumber',
                headerName: 'Invoice #',
                field: 'invoiceNumber',
                sortable: true,
                flex: 2,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
                cellRenderer: (params) => {
                    if (params.data) {
                        if (params.data.invoiceStatus === 'Approved'
                            || params.data.invoiceStatus === 'Exported') {
                            return (
                                <Button
                                    color="link"
                                    className="site-link align-baseline"
                                    disabled={!!this.context.formIsOpen}
                                    title="Print Invoice"
                                    onClick={() => {
                                        this.onPrintInvoice(params.data.id);
                                    }}
                                >
                                    {params.data.invoiceNumber}
                                    <FontAwesomeIcon
                                        className="ml-2"
                                        icon={faPrint}
                                        size="xs"
                                    />
                                </Button>
                            );
                        }

                        return params.data.invoiceNumber;
                    }

                    return null;
                },
            },
            {
                colId: 'timesheetNumber',
                headerName: 'Timesheet Number',
                field: 'timesheetNumber',
                sortable: false,
                flex: 2,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'customerName',
                headerName: 'Customer Name',
                field: 'customerName',
                sortable: true,
                flex: 2,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'Contract.EndCompany.CompanyName',
                headerName: 'End Customer',
                field: 'endCustomer',
                sortable: true,
                flex: 2,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'DispatchOfficeLocation.CompanyName', // leave this, it doesnt map to an ef prop but is triggered for the sort on the dynamic field.
                headerName: 'Class',
                field: 'dispatchOfficeLocationName',
                sortable: true,
                flex: 1.5,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'ContractNumber', // leave this, it doesnt map to an ef prop but is triggered for the sort on the dynamic field.
                headerName: 'Contract #',
                field: 'contractNumber',
                sortable: true,
                flex: 1.5,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'WorkOrderNumber',
                headerName: 'Work Order #',
                field: 'workOrderNumber',
                sortable: true,
                flex: 1.5,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'InvoiceStatusId',
                headerName: 'Invoice Status',
                field: 'invoiceStatus',
                sortable: true,
                flex: 1,
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: invoiceStatusFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: invoiceStatusFilterParams,
            },
            {
                colId: 'invoiceAmount',
                headerName: 'Invoice Amount',
                field: 'invoiceAmount',
                sortable: true,
                flex: 1,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'JobLocation',
                headerName: 'Job Address',
                field: 'jobAddress',
                sortable: false,
                flex: 2,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'Id',
                flex: 0,
                maxWidth: 75,
                headerName: 'Billing Issue',
                sortable: false,
                cellRenderer: (params) => {
                    if (params.data && params.data.invoiceStatus !== 'Deleted') {
                        return (
                            <IconButton
                                title="Reject"
                                type="button"
                                onClick={() => {
                                    this.onInvoiceIssuesSlideout(params.data);
                                }}
                            >
                                <FontAwesomeIcon icon={faComments} />
                            </IconButton>
                        );
                    }

                    return null;
                },
            },
            {
                colId: 'Id',
                flex: 0,
                maxWidth: 100,
                headerName: 'Invoice',
                sortable: false,
                cellRenderer: (params) => {
                    if (params.data && params.data.invoiceStatus !== 'Deleted') {
                        return (
                            <IconButton
                                type="button"
                                className="invoice-opener-btn"
                                onClick={() => {
                                    this.openInvoiceSlideout(params.data.id);
                                }}
                            >
                                <FontAwesomeIcon icon={faEye} />
                            </IconButton>
                        );
                    }

                    return null;
                },
            },
            {
                colId: 'HasOvertime',
                flex: 0,
                maxWidth: 120,
                headerName: 'Over 40',
                sortable: true,
                hide: !this.context.tenant.tenantSettings.enableBillPeriods || rentalInvoices,
                cellRenderer: (params) => {
                    if (params.data && params.data.invoiceStatus !== 'Deleted') {
                        if (
                            params.data.useCTRReport
                            && params.data.hasOvertime
                        ) {
                            return (
                                <IconButton
                                    type="button"
                                    size="lg"
                                    onClick={() => {
                                        this.onOver40Download(params.data);
                                    }}
                                >
                                    <FontAwesomeIcon icon={faFileExcel} />
                                </IconButton>
                            );
                        }
                    }
                    return null;
                },
            },
            {
                colId: 'Id',
                flex: 0,
                maxWidth: 115,
                headerName: 'Timesheets',
                sortable: false,
                hide: rentalInvoices,
                cellRenderer: (params) => {
                    if (params.data && params.data.invoiceStatus !== 'Deleted' && !params.data.isQuickSale && params.data.hasNonRentalTimesheets) {
                        return (
                            <IconButton
                                type="button"
                                style={{ border: 'none' }}
                                onClick={() => {
                                    this.onDownloadTimesheets(params.data);
                                }}
                            >
                                <FontAwesomeIcon icon={faFilePdf} />
                            </IconButton>
                        );
                    }
                    return null;
                },
            },
            {
                colId: 'Id',
                flex: 0,
                maxWidth: 100,
                headerName: 'Email',
                sortable: false,
                cellRenderer: (params) => {
                    if (
                        params.data
                        && (params.data.invoiceStatus === 'Approved'
                            || params.data.invoiceStatus === 'Exported')
                    ) {
                        return (
                            <IconButton
                                type="button"
                                onClick={() => {
                                    this.onEmailRowClick(params.data);
                                }}
                            >
                                <FontAwesomeIcon icon={faEnvelope} />
                            </IconButton>
                        );
                    }
                    return null;
                },
            },
            {
                colId: 'workId',
                headerName: 'Work Cards',
                field: 'ShowWorkCardDownload',
                flex: 0,
                maxWidth: 120,
                sortable: false,
                cellRenderer: (params) => {
                    if (params.data && params.data.showWorkCardDownload) {
                        return (
                            <IconButton
                                type="button"
                                onClick={() => {
                                    this.onDownloadWorkCards(params.data);
                                }}
                                disabled={this.state.isDownloadingWorkCards}
                                title="Download Work Cards"
                            >
                                <FontAwesomeIcon icon={faIdBadge} />
                            </IconButton>
                        );
                    }
                    return null;
                },
            },
            {
                colId: 'Id',
                flex: 0,
                maxWidth: 100,
                headerName: 'CTR File',
                sortable: false,
                hide: !this.context.tenant.tenantSettings.enableAlternativePayrollBilling || rentalInvoices,
                cellRenderer: (params) => {
                    if (params.data) {
                        if (
                            params.data.useCTRReport
                            && (params.data.invoiceStatus === 'Approved'
                                || params.data.invoiceStatus === 'Exported')
                        ) {
                            return (
                                <IconButton
                                    type="button"
                                    onClick={() => {
                                        this.onDownloadCTR(params.data);
                                    }}
                                >
                                    <FontAwesomeIcon icon={faFileExcel} />
                                </IconButton>
                            );
                        }
                    }
                    return null;
                },
            },
            {
                colId: 'Id',
                flex: 0,
                maxWidth: 100,
                headerName: 'Download',
                sortable: false,
                cellRenderer: (params) => {
                    if (params.data) {
                        if (
                            params.data.invoiceStatus === 'Approved'
                            || params.data.invoiceStatus === 'Exported'
                        ) {
                            return (
                                <IconButton
                                    type="button"
                                    onClick={() => {
                                        this.onDownloadInvoice(params.data);
                                    }}
                                >
                                    <FontAwesomeIcon icon={faDownload} />
                                </IconButton>
                            );
                        }
                    }
                    return null;
                },
            },
            {
                colId: 'InvoiceDownloadDate',
                headerName: 'Download Date',
                field: 'invoiceDownloadDate',
                sortable: true,
                flex: 1.5,
                tooltipField: 'invoiceDownloadBy',
                sort: { direction: 'asc', priority: 0 },
                filter: 'agDateColumnFilter',
                filterParams: DateFilterDefaults,
            },
            {
                colId: 'NetSuiteExportNumber',
                field: 'netSuiteExportNumber',
                flex: 0,
                headerName: 'NetSuite Export File',
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
                sortable: true,
                cellRenderer: (params) => {
                    if (params.data) {
                        if (params.data.exportedToNetSuite) {
                            return (
                                <button
                                    type="button"
                                    className="site-button-small btn btn-outline-primary btn-sm"
                                    style={{ border: 'none' }}
                                    onClick={() => {
                                        this.onGetExportFile(params.data.id, 'NetSuite');
                                    }}
                                >
                                    <i
                                        className="fa fa-file-archive fa-md mr-2"
                                    />
                                    <span>
                                        {params.data.netSuiteExportNumber}
                                    </span>
                                </button>
                            );
                        }
                    }
                    return null;
                },
            },
            {
                colId: 'NetSuiteSyncStatus',
                field: 'NetSuiteSyncStatus',
                flex: 0,
                headerName: 'NetSuite',
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: invoiceSyncStatusFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: invoiceSyncStatusFilterParams,
                sortable: false,
                cellRenderer: (params) => {
                    if (params.data) {
                        if (params.data.invoiceStatus === 'Approved' && this.canSyncToNetSuite() && (params.data.isRentalInvoice || params.data.invoiceEmailDate || params.data.invoiceDownloadDate)) {
                            const isSyncing = params.data.isSyncing || false;
                            return (
                                <div>
                                    <button
                                        type="button"
                                        className="site-button-small btn btn-outline-primary btn-sm"
                                        style={{ border: 'none' }}
                                        onClick={() => {
                                            this.syncInvoiceToNetSuite(params.data, params.rowIndex);
                                        }}
                                    >
                                        {isSyncing && (
                                            <FontAwesomeIcon
                                                icon={faCircleNotch}
                                                className="fa-spin mr-2"
                                                size="sm"
                                            />
                                        )}
                                        {!isSyncing && (
                                            <FontAwesomeIcon
                                                className="fa-md mr-2"
                                                icon={faPaperPlane}
                                                size="sm"
                                            />
                                        )}
                                        <span>Sync</span>
                                    </button>
                                </div>
                            );
                        }
                        if (params.data.invoiceStatus === 'Exported') {
                            return (
                                <IconButton
                                    type="button"
                                >
                                    <FontAwesomeIcon icon={faCheck} />
                                </IconButton>
                            );
                        }
                    }
                    return null;
                },
            },
            {
                colId: 'NetSuiteLogs',
                field: 'netSuiteLogs',
                flex: 0,
                headerName: 'NetSuite Logs',
                sortable: false,
                cellRenderer: (params) => {
                    if (params.data) {
                        if (params.data.invoiceVersion > 0) {
                            return (
                                <button
                                    type="button"
                                    className="site-button-small btn btn-outline-primary btn-sm"
                                    style={{ border: 'none' }}
                                    onClick={() => {
                                        this.openInvoiceNetSuiteLogs(params.data);
                                    }}
                                >
                                    <FontAwesomeIcon
                                        className="fa-md mr-2"
                                        icon={faHistory}
                                        size="sm"
                                    />
                                    <span>
                                        NetSuite Logs
                                    </span>
                                </button>
                            );
                        }
                    }
                    return null;
                },
            },
            {
                colId: 'SelectionPlaceholder',
                headerName: '',
                field: 'selectionPlaceholder',
                sortable: false,
                minWidth: 50,
                checkboxSelection: true,
            },
            {
                colId: 'ExportRevised',
                flex: 0,
                maxWidth: 150,
                headerName: 'Revised',
                sortable: true,
                cellRenderer: (params) => {
                    if (params.data) {
                        if (params.data.exportRevised == true) {
                            return (
                                <IconButton
                                    type="button"
                                >
                                    <FontAwesomeIcon icon={faCheck} />
                                </IconButton>
                            );
                        }
                    }
                    return null;
                },
            },
            {
                colId: 'InvoiceVersion',
                field: 'InvoiceVersion',
                flex: 0,
                maxWidth: 150,
                headerName: 'Version',
                sortable: true,
                cellRenderer: (params) => {
                    if (params.data) {
                        if (params.data.invoiceVersion > 0) {
                            return (`v${params.data.invoiceVersion}`);
                        }
                    }
                    return '';
                },
            },
            {
                colId: 'ModifiedByUser',
                headerName: 'Last Modified By',
                field: 'lastModifiedBy',
                sortable: true,
                flex: 5,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: '!ModifiedOn.HasValue ? CreatedOn : ModifiedOn',
                headerName: 'Last Modified On',
                field: 'lastModifiedOn',
                sortable: true,
                flex: 7,
                sort: { direction: 'asc', priority: 0 },
                filter: 'agDateColumnFilter',
                filterParams: DateFilterDefaults,
            },
        ];

        gridOptions.postProcessData = (data) => {
            context.setState({
                rowData: data.rows,
                scoreboardData: { ...data.counts } ?? null,
            });

            return { ...data };
        };

        const dataSource = createDataSource(
            ApiRoutes.invoice.search(rentalInvoices ?? false),
            gridOptions,
        );

        this.setState({
            loading: false,
            gridOptions,
            dataSource,
            isRejectingTimesheet: false,
            baseUrl,
            creditMemoTypeOptions,
        });
    };

    showInvoiceCreditMemoModal = () => {
        const { creditMemoTypeOptions } = this.state;
        Modal.show({
            title: 'Clear Invoice',
            onSubmit: (values) => this.syncInvoicesCMToNetSuite(values),
            SaveButtonProps: {
                label: 'Clear',
                savingLabel: 'Clearing',
                icon: faPaperPlane,
            },
            initialValues: {
                creditMemoDate: new Date(),
            },
            body: ({ values }) => (
                <>
                    <DatePicker
                        id="creditMemoDate"
                        label="Credit Memo Date"
                        name="creditMemoDate"
                        required
                        error="You must provide a credit memo date"
                    />
                    <ValidatedSelect
                        id="reason"
                        name="reason"
                        label="Reason"
                        options={CreditReasons}
                        required
                        error="You must provide a reason"
                    />
                    <ValidatedSelect
                        id="invoiceCreditMemoTypeId"
                        name="invoiceCreditMemoTypeId"
                        label="Was this due to a clerk error?"
                        options={creditMemoTypeOptions}
                        required
                        error="You must choose clerk error option"
                    />
                    <FormLabel
                        htmlFor="memo"
                        label="Memo"
                        required={values.reason == 'Other'}
                    />
                    <Input
                        id="memo"
                        name="memo"
                        required={values.reason == 'Other'}
                        error="You must provide a memo"
                    />
                </>
            ),
        });
    };

    render() {
        const {
            rowData,
            gridOptions,
            isDownloading,
            isDownloadingByContract,
            isEmailingByContract,
            disableDownloadByCustomer,
            invoiceIssueMessages,
            scoreboardData,
            selectedInvoice,
            timesheetNumbers,
            disableClear,
            disableExport,
            isSyncing,
            invoiceNetSuiteLogOpen,
            invoiceNetSuiteLogLoading,
            invoicesToBeSynced,
            willSyncInvoices,
            rentalInvoices,
            showInvoiceModal,
            rentalProjects,
            invoicing,
            billingDates,
            selectedProject,
            billingDate,
        } = this.state;

        let counts = {
            new: 0,
            draft: 0,
            approved: 0,
            pendingReview: 0,
            invoiceIssue: 0,
        };

        if (scoreboardData) {
            counts = scoreboardData;
        }

        const countNewColor = counts.new === 0 ? 'success' : 'warning';
        const countDraftColor = counts.draft === 0 ? 'success' : 'warning';
        const countApprovedColor = counts.approved === 0 ? 'success' : 'warning';
        const countPendingReviewColor = counts.pendingReview === 0 ? 'success' : 'warning';
        const countInvoiceIssueColor = counts.invoiceIssue === 0 ? 'success' : 'warning';

        const { errorResponse } = { ...this.state };

        // TODO: Eliminate inline styles.

        const { tenantSettings } = this.context.tenant;

        const minDate = selectedProject?.lastInvoiced ? moment(selectedProject?.lastInvoiced).add(2, 'day').toDate() : moment(selectedProject?.billingStartDate).add(1, 'day').toDate();

        if (this.state.loading || !tenantSettings) {
            return (
                <Progress />
            );
        }

        if (
            !tenantSettings.billingEnabled
            || !tenantSettings.invoicingEnabled
        ) return <NotAuthorizedPage />;

        return (
            <PageWrap>
                <PageHeading>
                    <FontAwesomeIcon
                        icon={faTasks}
                        className="mr-2 text-muted"
                    />
                    <span>{rentalInvoices && ('Rental ')}Invoice</span>
                    <span
                        style={{
                            float: 'right',
                            position: 'relative',
                            top: '-5px',
                        }}
                    >
                        {(rentalInvoices || tenantSettings.combineRentalNonRentalChargesOnRentalInvoices) && (
                            <Button
                                size="sm"
                                color="success"
                                style={{ marginRight: '10px' }}
                                onClick={() => this.toggleInvoiceModal()}
                                disabled={invoicing}
                            >
                                Invoice Project
                            </Button>
                        )}
                        <Button
                            size="sm"
                            color="success"
                            style={{ marginRight: '10px' }}
                            onClick={() => this.onDownloadInvoices()}
                            disabled={
                                disableDownloadByCustomer
                            }
                        >
                            <i
                                className="fa fa-download fa-md mr-2"
                            />
                            Download Invoices&nbsp;
                            {isDownloadingByContract && (
                                <FontAwesomeIcon
                                    icon={faCircleNotch}
                                    className="fa-spin mr-2"
                                    size="sm"
                                />
                            )}
                        </Button>

                        <Button
                            size="sm"
                            color="success"
                            style={{ marginRight: '10px' }}
                            onClick={() => this.onEmailButtonClick()}
                            disabled={
                                disableDownloadByCustomer
                            }
                        >
                            <i
                                className="fa fa-envelope fa-md mr-2"
                            />
                            Email Invoices&nbsp;
                            {isEmailingByContract && (
                                <FontAwesomeIcon
                                    icon={faCircleNotch}
                                    className="fa-spin mr-2"
                                    size="sm"
                                />
                            )}
                        </Button>

                        <Can
                            I="export"
                            a="invoice"
                        >
                            <Button
                                size="sm"
                                color="success"
                                style={{ marginRight: '10px' }}
                                onClick={() => this.onExportToNetSuite()}
                                disabled={
                                    disableExport
                                }
                            >
                                Export Invoices
                                {isDownloading && (
                                    <FontAwesomeIcon
                                        icon={faCircleNotch}
                                        className="fa-spin mr-2"
                                        size="sm"
                                    />
                                )}
                            </Button>
                        </Can>

                        {this.canSyncToNetSuite() && (
                            <Button
                                size="sm"
                                color="success"
                                style={{ marginRight: '10px' }}
                                onClick={() => this.syncInvoices()}
                                disabled={
                                    disableExport
                                    || isSyncing
                                }
                            >
                                Sync Invoices
                            </Button>
                        )}

                        {this.canClearInvoices() && (
                            <Button
                                size="sm"
                                color="danger"
                                style={{ marginRight: '10px' }}
                                onClick={() => this.showInvoiceCreditMemoModal()}
                                disabled={
                                    disableClear
                                    || isSyncing
                                }
                            >
                                Clear Invoices
                                {isSyncing && (
                                    <FontAwesomeIcon
                                        icon={faCircleNotch}
                                        className="fa-spin mr-2"
                                        size="sm"
                                    />
                                )}
                            </Button>
                        )}
                    </span>
                </PageHeading>

                <FlexCenterRow
                    className="pb-3 pt-2"
                    id="invoiceGenerationWorkflowStatus"
                >
                    <Badge
                        color={countNewColor}
                        style={{ minWidth: '100px' }}
                        className="mr-3 p-2"
                    >
                        New
                        <span className="ml-2">{counts.new}</span>
                    </Badge>
                    <Badge
                        color={countDraftColor}
                        style={{ minWidth: '100px' }}
                        className="mr-3 p-2"
                    >
                        Draft
                        <span className="ml-2">{counts.draft}</span>
                    </Badge>
                    <Badge
                        color={countApprovedColor}
                        style={{ minWidth: '100px' }}
                        className="mr-3 p-2"
                    >
                        Approved
                        <span className="ml-2">
                            {counts.approved}
                        </span>
                    </Badge>
                    <Badge
                        color={countInvoiceIssueColor}
                        style={{ minWidth: '100px' }}
                        className="mr-3 p-2"
                    >
                        Rejected
                        <span className="ml-2">
                            {counts.invoiceIssue}
                        </span>
                    </Badge>
                    <Badge
                        color={countPendingReviewColor}
                        style={{ minWidth: '100px' }}
                        className="mr-3 p-2"
                    >
                        Pending Review
                        <span className="ml-2">
                            {counts.pendingReview}
                        </span>
                    </Badge>
                </FlexCenterRow>

                <DataGridToolbar
                    entity="Billing"
                    gridApi={this.state.gridApi}
                    dataSource={this.state.dataSource}
                    hideAdd
                    hideExcelButton
                    gridOptions={this.state.gridOptions}
                    serverExport={{
                        apiPath: ApiRoutes.invoice.excelExport(rentalInvoices),
                        filePrefix: 'InvoiceSearch',
                    }}
                    serverExportDisabled={
                        !!this.state.saving
                        || !!this.state.loading
                        || !!this.state.loadingData
                    }
                >
                    <SmallButton
                        type="button"
                        onClick={this.getInvoiceChargesReport}
                        disabled={isDownloading}
                    >
                        <FontAwesomeIcon icon={faFileExcel} />
                        Export Invoice Charges
                        {isDownloading && (
                            <FontAwesomeIcon
                                icon={faCircleNotch}
                                className="fa-spin mr-2"
                                size="sm"
                            />
                        )}
                    </SmallButton>
                </DataGridToolbar>
                {!!this.state.loadingReport && (
                    <CustomCircularProgress />
                )}

                {!this.state.loadingReport && (
                    <DataGrid
                        domLayout="normal"
                        rowData={rowData}
                        gridOptions={gridOptions}
                        gridStatus={this.state.gridStatus}
                    />
                )}
                <FlexRow>
                    <FormErrorResponseDisplay
                        onClear={this.clearSaveErrors}
                        response={errorResponse}
                    />
                </FlexRow>
                <InvoiceSlideout
                    ref={this.invoiceSlideoutRef}
                    show={this.state.invoiceSlideoutOpen}
                    toggleShow={(open) => this.setState({ invoiceSlideoutOpen: open })}
                    onClose={this.onInvoiceSlideoutClosed}
                />

                <Alert
                    isOpen={this.state.isDownloading}
                    title="Download Invoice"
                >
                    <FontAwesomeIcon
                        icon={faCircleNotch}
                        className="fa-spin mr-2"
                        size="sm"
                    />
                    &nbsp;Processing...
                </Alert>

                <Alert
                    isOpen={this.state.isDownloadingTimesheets}
                    title="Download Timesheets"
                >
                    <FontAwesomeIcon
                        icon={faCircleNotch}
                        className="fa-spin mr-2"
                        size="sm"
                    />
                    &nbsp;Processing...
                </Alert>

                <Alert
                    isOpen={this.state.isDownloadingOver40}
                    title="Download Proof of 40"
                >
                    <FontAwesomeIcon
                        icon={faCircleNotch}
                        className="fa-spin mr-2"
                        size="sm"
                    />
                    &nbsp;Processing...
                </Alert>

                <Alert
                    isOpen={this.state.isDownloadingCTR}
                    title="Download CTR File"
                >
                    <FontAwesomeIcon
                        icon={faCircleNotch}
                        className="fa-spin mr-2"
                        size="sm"
                    />
                    &nbsp;Processing...
                </Alert>

                <Alert
                    isOpen={this.state.isEmailing}
                    title="Generate Attachments for Email"
                >
                    <FontAwesomeIcon
                        icon={faCircleNotch}
                        className="fa-spin mr-2"
                        size="sm"
                    />
                    &nbsp;Processing...
                </Alert>

                <Alert
                    isOpen={willSyncInvoices}
                    title="Sync Invoices To NetSuite Export"
                    toggle={() => this.setState({
                        willSyncInvoices: false,
                    })}
                >
                    {invoicesToBeSynced.length === 0 ? (
                        <p>
                            {' '}
                            There are no invoices that can be synced
                        </p>
                    ) : (
                        <div
                            style={{
                                maxHeight: '500px',
                                overflowY: 'auto',
                            }}
                        >
                            {invoicesToBeSynced
                                .sort()
                                .map((invn) => (
                                    <div key={invn.id}>
                                        {(invn.status === 'todo' || invn.status === 'loading') && (
                                            <FontAwesomeIcon
                                                icon={faCircleNotch}
                                                className="fa-spin mr-2"
                                                size="sm"
                                            />
                                        )}
                                        {invn.status === 'done' && (
                                            <FontAwesomeIcon
                                                color="success"
                                                icon={faCheck}
                                                className="mr-2"
                                                size="sm"
                                            />
                                        )}
                                        {invn.status === 'error' && (
                                            <FontAwesomeIcon
                                                color="error"
                                                icon={faClose}
                                                className="mr-2"
                                                size="sm"
                                            />
                                        )}
                                        <b>{invn.invoiceNumber}</b>
                                        {invn.status === 'error' && (
                                            <span>: {invn.error}</span>
                                        )}
                                    </div>
                                ))}
                        </div>
                    )}
                </Alert>

                <Alert
                    size="xl"
                    isOpen={invoiceNetSuiteLogOpen}
                    toggle={() => this.handleCloseNetSuiteLog()}
                    title="Invoice NetSuite Log"
                    scrollable
                >
                    {invoiceNetSuiteLogLoading ? (
                        <span> Loading...</span>
                    ) : (
                        <div>
                            <table className="table table-sm table-bordered table-striped">
                                <thead className="text-muted">
                                    <tr>
                                        <th>Transaction Number</th>
                                        <th>Date</th>
                                        <th>Posting Period</th>
                                        <th>Total Amount</th>
                                        <th>Paid</th>
                                        <th>Sync Date</th>
                                        <th>Type</th>
                                        <th>NS File</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {this.state.netsuiteLogs.length === 0 ? (
                                        <tr>
                                            <td
                                                colSpan="7"
                                                className="text-center"
                                            >
                                                No Invoice Sync.
                                            </td>
                                        </tr>
                                    ) : (
                                        this.state.netsuiteLogs.map((trans, index) => (
                                            <tr key={index}>
                                                <td>
                                                    <a
                                                        className="site-link"
                                                        href={`${trans.link}.nl?id=${trans.id}`}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        {trans.tranid}</a>
                                                </td>
                                                <td>{trans.trandate}</td>
                                                <td>{trans.periodname}</td>
                                                <td>${trans.estgrossprofit}</td>
                                                <td>{trans.estgrossprofit == trans.foreignamountpaid ? 'Yes' : 'No'}</td>
                                                <td>{trans.createddate}</td>
                                                <td>{trans.recordtype === 'invoice' ? ('Invoice') : ('Credit Memo')}</td>
                                                <td>
                                                    <a
                                                        className="site-link"
                                                        href={`${trans.filelink}`}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        <FontAwesomeIcon
                                                            className="ml-2"
                                                            icon={faPrint}
                                                            size="xs"
                                                        />
                                                    </a>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Alert>

                <ModalForm
                    isOpen={showInvoiceModal}
                    onSubmit={() => this.onGenerateInvoice()}
                    toggle={() => this.setState({
                        showInvoiceModal: false,
                    })}
                    title="Invoice Project"
                    SaveButtonProps={{
                        label: 'Generate Invoice',
                        savingLabel: 'Generating',
                        saving: invoicing,
                        disabled: invoicing || !billingDate,
                    }}
                >
                    <ValidatedSelect
                        id="projectId"
                        name="projectId"
                        label="Project"
                        options={rentalProjects}
                        value={
                            (rentalProjects ?? []).find(
                                (p) => p.value === selectedProject?.value,
                            ) ?? ''
                        }
                        onChange={(p) => {
                            this.onProjectSelected(p);
                        }}
                    />
                    <TipAlert
                        tipText="Note"
                        label="Leave the project field empty to invoice all projects with a billing date matching the selected billing date."
                    />

                    <DatePicker
                        id="billingDate"
                        label="Billing Date"
                        name="billingDate"
                        selected={billingDate}
                        className="form-control"
                        minDate={selectedProject ? minDate : null}
                        maxDate={this.getMaxBillingDate()}
                        highlightDates={billingDates}
                        required
                        error="A Billing Date is required."
                        filterDate={(date) => {
                            if (!selectedProject) return true;
                            const isHighlighted = billingDates.some(
                                (d) => moment(d).isSame(date, 'day'),
                            );
                            return isHighlighted;
                        }}
                        onChange={(_, date) => {
                            this.setState({ billingDate: date });
                        }}
                    />
                </ModalForm>



                <SlideForm
                    loading={false}
                    show={this.state.showInvoiceIssuesSlideout}
                    id="invoiceIssuesSlideout"
                    formIcon={faComments}
                    formTitle="Invoice Issues"
                    onClose={() => {
                        this.setState({
                            showInvoiceIssuesSlideout: false,
                        });
                    }}
                >
                    <InvoiceIssuesWidget
                        ref={this.invoiceIssuesSlideoutRef}
                        invoice={selectedInvoice}
                        messages={invoiceIssueMessages}
                        timesheetNumbers={timesheetNumbers}
                        context="rejecting"
                        onRejectCallback={(invoiceId, timesheetId, message, updateStatus) => {
                            this.onRejectToInvoiceIssues(invoiceId, timesheetId, message, updateStatus);
                        }}
                        onAcceptCallback={(
                            invoiceId,
                            timesheetId,
                            message,
                        ) => {
                            this.onAcceptInvoiceIssueReply(
                                invoiceId,
                                timesheetId,
                                message,
                            );
                        }}
                        onCloseCallback={() => {
                            this.setState({
                                showInvoiceIssuesSlideout: false,
                            });
                        }}
                    />
                </SlideForm>
            </PageWrap>
        );
    }
}

export default InvoiceIndex;